import api from '@/services/api';
import { setRawCookie, removeCookie } from '@/services/helpers/cookie';

const DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN;
/**
 ** Disconnect a user
 * Remove customer token from cookies
 * and change boolean
 */
async function disconnect() {
  removeCookie('access_token', { domain: DOMAIN });
  removeCookie('refresh_token', { domain: DOMAIN });
  await api().initialize();
}

/**
 ** Retrieve me info
 */
const clientMe = () => api()
  .get('accounts/me');

/**
 ** Retrieve user info
 */
const account = (accountId) => new Promise((resolve, reject) => {
  api().get(`/accounts/${accountId}`)
    .then(resolve)
    .catch((err) => {
      removeCookie('access_token', { domain: DOMAIN });
      removeCookie('refresh_token', { domain: DOMAIN });
      reject(err);
    });
});

/**
 ** Log in an user
 * @param {String} email
 * @param {String} password
 */
const login = async (email, password) => {
  try {
    await api().initialize();
    const response = await api()
      .post('accounts/login', { email, password });
    setRawCookie('access_token', response.data.access_token, { domain: DOMAIN });
    setRawCookie('refresh_token', response.data.refresh_token, { domain: DOMAIN });
    await api().setAuthorization(response.data.access_token);
  } catch (err) {
    removeCookie('access_token', { domain: DOMAIN });
    removeCookie('refresh_token', { domain: DOMAIN });
    throw (err);
  }
};

/**
 ** Log in an user by token id
 * @param {String} email
 * @param {String} password
 */
const autoLogin = async (autoLoginToken) => {
  try {
    const response = await api().post(`accounts/autologin/${autoLoginToken}`);
    const { access_token, refresh_token } = response.data;
    setRawCookie('access_token', access_token, { expires: 30, domain: DOMAIN });
    if (refresh_token) {
      setRawCookie('refresh_token', refresh_token, { expires: 30, domain: DOMAIN });
    }
    await api().setAuthorization(access_token);
    setRawCookie('isLogged', true);
  } catch (err) {
    removeCookie('access_token', { domain: DOMAIN });
    removeCookie('refresh_token', { domain: DOMAIN });
    removeCookie('isLogged');
    throw (err);
  }
};

const updateUser = (_user, id) => api()
  .put(`/users/${id}`, _user);

const updateAccount = (_account, id) => api()
  .patch(`/accounts/${id}`, _account);

/**
 ** Send a link to user to reset password
 * @param {String} email
 */
const resetLink = (email) => api()
  .put('/users/recover-password', { email });

/**
 ** Change password for a user
 */
const resetPassword = ({
  token, password, confirmPassword, id,
}) => api().put(`users/${id}/reset-password`, { token, password, confirmPassword });

/**
 ** Update password for a user
 */
const updatePassword = (_infos, id) => api()
  .put(`users/${id}/update-password`, _infos);

const getSponsorshipsFromClient = (id) => api()
  .get(`users/${id}/referrals`);

const addSponsorshipsToClient = (userId, referral, params = '?origin=dashboard') => api()
  .post(`users/${userId}/referrals${params}`, referral);

const getSponsorshipPool = (id) => api()
  .get(`users/${id}/pool`);

const resendEmail = (id, referralId) => api()
  .post(`/users/${id}/referrals/${referralId}/relaunch`);

const findMeetingById = (id) => api()
  .get(`users/${id}/meetings`);

const cancelMeetingById = (id) => api()
  .post(`meetings/${id}/cancel`);

const createMember = (member) => api()
  .post('accounts', { ...member });

const deleteMemberById = (id) => api()
  .delete(`users/${id}`);

const switchAccount = (id) => api()
  .patch(`accounts/${id}/switch`);

export {
  login,
  account,
  resetLink,
  resendEmail,
  resetPassword,
  updatePassword,
  getSponsorshipPool,
  getSponsorshipsFromClient,
  addSponsorshipsToClient,
  updateAccount,
  updateUser,
  disconnect,
  cancelMeetingById,
  findMeetingById,
  createMember,
  deleteMemberById,
  clientMe,
  switchAccount,
  autoLogin,
};
